import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Separator } from '../ui/separator';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from '../ui/sheet';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { Alert, AlertDescription } from '../ui/alert';
import {
  AlertTriangle, Building2, CheckCircle2, Mail, Phone, Plus, RefreshCw, Search, Trash2, Users,
} from 'lucide-react';
import {
  PROSPECT_STAGES, countByStage, groupByCountry, isOverdue, stageLabel,
} from '../../lib/prospects';

const COPY = {
  fr: {
    title: 'Prospection',
    lede: 'Entreprises candidates à l\'embarquement, suivies pays par pays.',
    newProspect: 'Nouveau dossier',
    refresh: 'Actualiser',
    search: 'Société ou interlocuteur…',
    allCountries: 'Tous les pays',
    allStages: 'Toutes les étapes',
    overdueOnly: 'Relances en retard',
    kpiOpen: 'Dossiers ouverts',
    kpiOverdue: 'Relances en retard',
    kpiEngaged: 'Démo ou essai',
    kpiWon: 'Embarquées',
    kpiOpenHint: 'hors gagnés et perdus',
    kpiOverdueHint: 'à relancer aujourd\'hui',
    kpiEngagedHint: 'dossiers avancés',
    kpiWonHint: 'entreprises clientes',
    empty: 'Aucun dossier de prospection.',
    emptyHint: 'Les demandes de démo du site ouvrent un dossier automatiquement.',
    emptyFiltered: 'Aucun dossier ne correspond aux filtres.',
    files: 'dossiers',
    file: 'dossier',
    overdue: 'en retard',
    noNextAction: 'Aucune relance planifiée',
    company: 'Société',
    contact: 'Interlocuteur',
    stage: 'Étape',
    nextAction: 'Prochaine relance',
    volume: 'Colis / mois',
    volumeLong: 'Colis par mois (estimé)',
    notes: 'Notes',
    addNote: 'Ajouter une note',
    notePlaceholder: 'Ce qui s\'est dit, ce qui a été convenu…',
    save: 'Enregistrer',
    cancel: 'Annuler',
    lostReason: 'Motif de la perte',
    lostReasonRequired: 'Indiquez pourquoi le dossier est perdu.',
    country: 'Pays',
    city: 'Ville',
    email: 'E-mail',
    phone: 'Téléphone',
    create: 'Créer le dossier',
    history: 'Historique',
    noContact: 'Pas d\'interlocuteur renseigné',
    delete: 'Supprimer',
    deleteTitle: 'Supprimer ce dossier ?',
    deleteBody: 'Le dossier, ses notes et son historique seront perdus. Pour garder la trace d\'un refus, passez plutôt le dossier en « perdu » avec son motif.',
    deleteConverted: 'Dossier converti en société cliente : il documente son origine et ne peut pas être supprimé.',
    date: 'Date',
    sources: { demo_request: 'Demande de démo', manual: 'Saisie manuelle', import: 'Import' },
  },
  en: {
    title: 'Prospecting',
    lede: 'Freight companies in onboarding, followed country by country.',
    newProspect: 'New file',
    refresh: 'Refresh',
    search: 'Company or contact…',
    allCountries: 'All countries',
    allStages: 'All stages',
    overdueOnly: 'Overdue follow-ups',
    kpiOpen: 'Open files',
    kpiOverdue: 'Overdue follow-ups',
    kpiEngaged: 'Demo or trial',
    kpiWon: 'Onboarded',
    kpiOpenHint: 'excluding won and lost',
    kpiOverdueHint: 'due today',
    kpiEngagedHint: 'advanced files',
    kpiWonHint: 'customer companies',
    empty: 'No prospect yet.',
    emptyHint: 'Demo requests from the website open a file automatically.',
    emptyFiltered: 'No file matches these filters.',
    files: 'files',
    file: 'file',
    overdue: 'overdue',
    noNextAction: 'No follow-up planned',
    company: 'Company',
    contact: 'Contact',
    stage: 'Stage',
    nextAction: 'Next follow-up',
    volume: 'Packages / month',
    volumeLong: 'Packages per month (estimate)',
    notes: 'Notes',
    addNote: 'Add a note',
    notePlaceholder: 'What was said, what was agreed…',
    save: 'Save',
    cancel: 'Cancel',
    lostReason: 'Reason for loss',
    lostReasonRequired: 'State why the file is lost.',
    country: 'Country',
    city: 'City',
    email: 'Email',
    phone: 'Phone',
    create: 'Create file',
    history: 'History',
    noContact: 'No contact recorded',
    delete: 'Delete',
    deleteTitle: 'Delete this file?',
    deleteBody: 'The file, its notes and its history will be lost. To keep a record of a refusal, mark the file as lost with its reason instead.',
    deleteConverted: 'This file was converted into a customer company: it documents its origin and cannot be deleted.',
    date: 'Date',
    sources: { demo_request: 'Demo request', manual: 'Manual entry', import: 'Import' },
  },
};

const STAGE_COLORS = {
  new: 'bg-muted text-foreground',
  contacted: 'bg-accent/10 text-accent',
  demo_done: 'bg-violet-100 text-violet-800',
  trial: 'bg-yellow-100 text-yellow-800',
  won: 'bg-green-100 text-green-800',
  lost: 'bg-red-100 text-red-800',
};

const ALL = '__all__';

const formatDate = (value, language) => (value
  ? new Date(value).toLocaleDateString(language === 'en' ? 'en-GB' : 'fr-FR',
    { day: '2-digit', month: 'short', year: 'numeric' })
  : null);

const StageBadge = ({ stage, language }) => (
  <Badge className={STAGE_COLORS[stage] || STAGE_COLORS.new}>{stageLabel(stage, language)}</Badge>
);

const KpiCard = ({ label, value, hint, icon: Icon, tone }) => (
  <Card className={tone === 'alert' && value > 0 ? 'border-red-200' : undefined}>
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <CardTitle className="text-sm font-medium">{label}</CardTitle>
      <Icon className={`h-4 w-4 ${tone === 'alert' && value > 0 ? 'text-red-500' : 'text-muted-foreground'}`} />
    </CardHeader>
    <CardContent>
      <div className={`text-2xl font-bold ${tone === 'alert' && value > 0 ? 'text-red-600' : ''}`}>{value}</div>
      <p className="text-xs text-muted-foreground">{hint}</p>
    </CardContent>
  </Card>
);

const ProspectionTab = () => {
  const { language } = useLanguage();
  const copy = COPY[language === 'en' ? 'en' : 'fr'];

  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [countryFilter, setCountryFilter] = useState(ALL);
  const [stageFilter, setStageFilter] = useState(ALL);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);

  const fetchProspects = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/prospects');
      setProspects(res.data || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProspects(); }, [fetchProspects]);

  // Une seule référence de temps par chargement : le retard ne clignote pas au re-rendu.
  const now = useMemo(() => new Date(), [prospects]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return prospects.filter((p) => {
      if (countryFilter !== ALL && p.country !== countryFilter) return false;
      if (stageFilter !== ALL && p.stage !== stageFilter) return false;
      if (overdueOnly && !isOverdue(p, now)) return false;
      if (!query) return true;
      return [p.company_name, p.contact_name, p.contact_email]
        .some((v) => v?.toLowerCase().includes(query));
    });
  }, [prospects, search, countryFilter, stageFilter, overdueOnly, now]);

  const groups = useMemo(() => groupByCountry(filtered, now), [filtered, now]);
  const stageTotals = useMemo(() => countByStage(prospects), [prospects]);
  const overdueTotal = useMemo(
    () => prospects.filter((p) => isOverdue(p, now)).length, [prospects, now],
  );
  const openTotal = prospects.length - stageTotals.won - stageTotals.lost;
  const countries = useMemo(
    () => [...new Set(prospects.map((p) => p.country).filter(Boolean))].sort(),
    [prospects],
  );
  const hasFilters = countryFilter !== ALL || stageFilter !== ALL || overdueOnly || search.trim();

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">{copy.title}</h2>
          <p className="text-sm text-muted-foreground">{copy.lede}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchProspects}>
            <RefreshCw className="h-4 w-4 mr-1" /> {copy.refresh}
          </Button>
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4 mr-1" /> {copy.newProspect}
          </Button>
        </div>
      </div>

      {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label={copy.kpiOpen} value={openTotal} hint={copy.kpiOpenHint} icon={Building2} />
        <KpiCard
          label={copy.kpiOverdue} value={overdueTotal} hint={copy.kpiOverdueHint}
          icon={AlertTriangle} tone="alert"
        />
        <KpiCard
          label={copy.kpiEngaged} value={stageTotals.demo_done + stageTotals.trial}
          hint={copy.kpiEngagedHint} icon={Users}
        />
        <KpiCard label={copy.kpiWon} value={stageTotals.won} hint={copy.kpiWonHint} icon={CheckCircle2} />
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-center gap-2 p-3">
          <div className="relative min-w-[200px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder={copy.search}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Select value={countryFilter} onValueChange={setCountryFilter}>
            <SelectTrigger className="w-full sm:w-[190px]">
              <SelectValue placeholder={copy.allCountries} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{copy.allCountries}</SelectItem>
              {countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={stageFilter} onValueChange={setStageFilter}>
            <SelectTrigger className="w-full sm:w-[190px]">
              <SelectValue placeholder={copy.allStages} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{copy.allStages}</SelectItem>
              {PROSPECT_STAGES.map(({ key }) => (
                <SelectItem key={key} value={key}>
                  {stageLabel(key, language)} ({stageTotals[key]})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant={overdueOnly ? 'default' : 'outline'}
            size="sm"
            onClick={() => setOverdueOnly((v) => !v)}
            className="h-10"
          >
            <AlertTriangle className="h-4 w-4 mr-1" />
            {copy.overdueOnly}
            {overdueTotal > 0 && <span className="ml-1 font-semibold">({overdueTotal})</span>}
          </Button>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-16">
          <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : groups.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Building2 className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="font-medium">{hasFilters ? copy.emptyFiltered : copy.empty}</p>
            {!hasFilters && <p className="mt-1 text-sm text-muted-foreground">{copy.emptyHint}</p>}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {groups.map((group) => (
            <Card key={group.country}>
              <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0 py-3">
                <CardTitle className="text-base">{group.country}</CardTitle>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  {group.overdue > 0 && (
                    <Badge className="bg-red-100 text-red-800">
                      {group.overdue} {copy.overdue}
                    </Badge>
                  )}
                  <span>{group.total} {group.total > 1 ? copy.files : copy.file}</span>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{copy.company}</TableHead>
                        <TableHead className="hidden md:table-cell">{copy.contact}</TableHead>
                        <TableHead>{copy.stage}</TableHead>
                        <TableHead className="hidden sm:table-cell">{copy.nextAction}</TableHead>
                        <TableHead className="hidden lg:table-cell text-right">{copy.volume}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {group.prospects.map((prospect) => {
                        const overdue = isOverdue(prospect, now);
                        return (
                          <TableRow
                            key={prospect.id}
                            onClick={() => setSelected(prospect)}
                            className="cursor-pointer"
                          >
                            <TableCell className="font-medium">
                              {prospect.company_name}
                              {prospect.city && (
                                <span className="block text-xs text-muted-foreground">{prospect.city}</span>
                              )}
                            </TableCell>
                            <TableCell className="hidden md:table-cell text-muted-foreground">
                              {prospect.contact_name || '—'}
                            </TableCell>
                            <TableCell><StageBadge stage={prospect.stage} language={language} /></TableCell>
                            <TableCell className="hidden sm:table-cell">
                              {prospect.next_action ? (
                                <span className={overdue ? 'font-medium text-red-600' : 'text-muted-foreground'}>
                                  {prospect.next_action}
                                  {prospect.next_action_date && (
                                    <span className="block text-xs">
                                      {formatDate(prospect.next_action_date, language)}
                                    </span>
                                  )}
                                </span>
                              ) : (
                                <span className="text-xs text-muted-foreground">{copy.noNextAction}</span>
                              )}
                            </TableCell>
                            <TableCell className="hidden lg:table-cell text-right text-muted-foreground">
                              {prospect.estimated_monthly_packages || '—'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ProspectSheet
        prospect={selected}
        copy={copy}
        language={language}
        onClose={() => setSelected(null)}
        onSaved={() => { setSelected(null); fetchProspects(); }}
      />

      {creating && (
        <CreateProspectDialog
          copy={copy}
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); fetchProspects(); }}
        />
      )}
    </div>
  );
};

/** Fiche du dossier en panneau latéral : étape, relance, notes, historique. */
const ProspectSheet = ({ prospect, copy, language, onClose, onSaved }) => {
  const [stage, setStage] = useState('new');
  const [lostReason, setLostReason] = useState('');
  const [nextAction, setNextAction] = useState('');
  const [nextDate, setNextDate] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState('');

  // Le panneau reste monté : on le resynchronise sur le dossier ouvert.
  useEffect(() => {
    if (!prospect) return;
    setStage(prospect.stage);
    setLostReason(prospect.lost_reason || '');
    setNextAction(prospect.next_action || '');
    setNextDate(prospect.next_action_date?.slice(0, 10) || '');
    setNote('');
    setError('');
    setSaving(false);
    setDeleting(false);
    setConfirmDelete(false);
  }, [prospect]);

  if (!prospect) return null;

  const save = async () => {
    if (stage === 'lost' && !lostReason.trim()) {
      setError(copy.lostReasonRequired);
      return;
    }
    setSaving(true);
    setError('');
    try {
      await api.put(`/api/prospects/${prospect.id}`, {
        stage,
        lost_reason: stage === 'lost' ? lostReason.trim() : undefined,
        next_action: nextAction || undefined,
        next_action_date: nextDate ? new Date(nextDate).toISOString() : undefined,
      });
      if (note.trim()) {
        await api.post(`/api/prospects/${prospect.id}/notes`, { text: note.trim() });
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur');
      setSaving(false);
    }
  };

  const remove = async () => {
    setDeleting(true);
    setError('');
    try {
      await api.delete(`/api/prospects/${prospect.id}`);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur');
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const converted = Boolean(prospect.company_id);

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto p-0">
        <div className="space-y-5 p-6 pb-24">
          <SheetHeader className="space-y-1 text-left">
            <SheetTitle>{prospect.company_name}</SheetTitle>
            <SheetDescription>
              {[prospect.country, prospect.city, copy.sources[prospect.source] || prospect.source]
                .filter(Boolean).join(' · ')}
            </SheetDescription>
          </SheetHeader>

          {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

          <div className="rounded-lg border border-border p-3 text-sm">
            {prospect.contact_name || prospect.contact_email || prospect.contact_phone ? (
              <div className="space-y-1.5">
                {prospect.contact_name && <p className="font-medium">{prospect.contact_name}</p>}
                {prospect.contact_email && (
                  <a href={`mailto:${prospect.contact_email}`}
                    className="flex items-center gap-2 text-muted-foreground hover:text-accent">
                    <Mail className="h-3.5 w-3.5" />{prospect.contact_email}
                  </a>
                )}
                {prospect.contact_phone && (
                  <a href={`tel:${prospect.contact_phone}`}
                    className="flex items-center gap-2 text-muted-foreground hover:text-accent">
                    <Phone className="h-3.5 w-3.5" />{prospect.contact_phone}
                  </a>
                )}
              </div>
            ) : (
              <p className="text-muted-foreground">{copy.noContact}</p>
            )}
            {prospect.estimated_monthly_packages > 0 && (
              <p className="mt-2 border-t border-border pt-2 text-muted-foreground">
                {copy.volumeLong} : <strong className="text-foreground">{prospect.estimated_monthly_packages}</strong>
              </p>
            )}
          </div>

          <Separator />

          <div className="space-y-2">
            <Label>{copy.stage}</Label>
            <Select value={stage} onValueChange={setStage}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {PROSPECT_STAGES.map(({ key }) => (
                  <SelectItem key={key} value={key}>{stageLabel(key, language)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {stage === 'lost' && (
            <div className="space-y-2">
              <Label>{copy.lostReason} *</Label>
              <Input value={lostReason} onChange={(e) => setLostReason(e.target.value)} />
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
            <div className="space-y-2">
              <Label>{copy.nextAction}</Label>
              <Input value={nextAction} onChange={(e) => setNextAction(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>{copy.date}</Label>
              <Input type="date" value={nextDate} onChange={(e) => setNextDate(e.target.value)} />
            </div>
          </div>

          <Separator />

          <div className="space-y-2">
            <Label>{copy.addNote}</Label>
            <Textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={copy.notePlaceholder}
            />
          </div>

          {prospect.notes?.length > 0 && (
            <div className="space-y-2">
              <Label>{copy.notes}</Label>
              <ul className="space-y-2">
                {[...prospect.notes].reverse().map((n) => (
                  <li key={n.id} className="rounded-md bg-muted/50 p-3 text-sm">
                    <p className="whitespace-pre-wrap">{n.text}</p>
                    <p className="mt-1.5 text-xs text-muted-foreground">
                      {n.author_name} · {formatDate(n.created_at, language)}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {prospect.stage_history?.length > 0 && (
            <div className="space-y-2">
              <Label>{copy.history}</Label>
              <ul className="space-y-1.5 text-xs">
                {[...prospect.stage_history].reverse().map((h, i) => (
                  <li key={i} className="flex flex-wrap items-center gap-1.5 text-muted-foreground">
                    <span>{formatDate(h.changed_at, language)}</span>
                    <StageBadge stage={h.from_stage} language={language} />
                    <span>→</span>
                    <StageBadge stage={h.to_stage} language={language} />
                    {h.note && <span className="w-full">{h.note}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Barre d'action toujours visible : la fiche défile, l'enregistrement non. */}
        <div className="sticky bottom-0 flex items-center gap-2 border-t border-border bg-background p-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setConfirmDelete(true)}
            disabled={converted || deleting}
            title={converted ? copy.deleteConverted : copy.delete}
            aria-label={copy.delete}
            className="text-muted-foreground hover:text-red-600"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
          <Button variant="outline" className="flex-1" onClick={onClose}>{copy.cancel}</Button>
          <Button className="flex-1" onClick={save} disabled={saving || deleting}>
            {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : copy.save}
          </Button>
        </div>

        <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{copy.deleteTitle}</AlertDialogTitle>
              <AlertDialogDescription>
                <strong>{prospect.company_name}</strong> — {copy.deleteBody}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{copy.cancel}</AlertDialogCancel>
              <AlertDialogAction
                onClick={remove}
                className="bg-red-600 text-white hover:bg-red-700"
              >
                {copy.delete}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SheetContent>
    </Sheet>
  );
};

/** Dossier saisi à la main, après un salon ou une recommandation. */
const CreateProspectDialog = ({ copy, onClose, onCreated }) => {
  const [form, setForm] = useState({
    company_name: '', country: '', city: '', contact_name: '',
    contact_email: '', contact_phone: '', estimated_monthly_packages: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.post('/api/prospects', {
        ...form,
        estimated_monthly_packages: form.estimated_monthly_packages
          ? parseInt(form.estimated_monthly_packages, 10)
          : undefined,
        contact_email: form.contact_email || undefined,
      });
      onCreated();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur');
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-lg">
        <form onSubmit={submit}>
          <DialogHeader><DialogTitle>{copy.newProspect}</DialogTitle></DialogHeader>

          <div className="space-y-3 py-4">
            {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
            <div className="space-y-2">
              <Label>{copy.company} *</Label>
              <Input required value={form.company_name} onChange={set('company_name')} />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>{copy.country} *</Label>
                <Input required value={form.country} onChange={set('country')} />
              </div>
              <div className="space-y-2">
                <Label>{copy.city}</Label>
                <Input value={form.city} onChange={set('city')} />
              </div>
              <div className="space-y-2">
                <Label>{copy.contact}</Label>
                <Input value={form.contact_name} onChange={set('contact_name')} />
              </div>
              <div className="space-y-2">
                <Label>{copy.phone}</Label>
                <Input value={form.contact_phone} onChange={set('contact_phone')} />
              </div>
              <div className="space-y-2">
                <Label>{copy.email}</Label>
                <Input type="email" value={form.contact_email} onChange={set('contact_email')} />
              </div>
              <div className="space-y-2">
                <Label>{copy.volumeLong}</Label>
                <Input type="number" min="0" value={form.estimated_monthly_packages}
                  onChange={set('estimated_monthly_packages')} />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="submit" disabled={saving} className="w-full sm:w-auto">{copy.create}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProspectionTab;
